import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  FilterList as FilterListIcon,
  VerticalAlignBottom as FollowIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import type { ExecutionStep } from '@/types';

import { LogResultTable } from './LogResultTable';

type StepFilter = 'all' | 'success' | 'error' | 'running';

interface LogViewerProps {
  steps: ExecutionStep[] | undefined;
  isRunning: boolean;
  onRefresh?: () => void;
  isFetching?: boolean;
  showHeader?: boolean;
  maxHeight?: string | number;
  /** Compact mode for bottom panels */
  compact?: boolean;
  appId?: string;
  environmentId?: string;
}

const StepRow = React.memo(
  ({
    step,
    index,
    isRunning,
    appId,
    environmentId,
  }: {
    step: ExecutionStep;
    index: number;
    isRunning: boolean;
    appId?: string;
    environmentId?: string;
  }) => {
    const isSchema = step.statement?.includes('printschema');
    const isTable =
      !isSchema && (step.statement?.includes('print') || step.statement?.includes('PRINT'));
    const hasExtendedProps = step.extendedProperties && step.extendedProperties !== '';
    const showAsRunning = isRunning && !step.completed;

    return (
      <Box
        sx={{
          display: 'flex',
          p: '6px 8px',
          fontSize: 13,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: index % 2 === 0 ? 'transparent' : 'action.hover',
          borderLeft: step.succeeded === false && step.completed ? '3px solid' : 'none',
          borderLeftColor: 'error.main',
          alignItems: 'flex-start',
          gap: 1,
          '&:hover': { bgcolor: 'action.selected' },
        }}
      >
        {/* Status icon */}
        <Box sx={{ width: 24, flexShrink: 0, pt: 0.25 }}>
          {(() => {
            if (showAsRunning) return <CircularProgress size={16} />;
            if (step.succeeded) return <CheckIcon color="success" sx={{ fontSize: 18 }} />;
            return <ErrorIcon color="error" sx={{ fontSize: 18 }} />;
          })()}
        </Box>

        {/* Timestamp - hidden for in-progress steps since the backend's startTime
            for an unfinished step is a placeholder (the execution kick-off time)
            and would otherwise appear older than already-completed steps. */}
        {step.startTime && (
          <Box
            sx={{
              width: 85,
              flexShrink: 0,
              color: 'text.secondary',
              fontSize: 11,
              fontStyle: showAsRunning ? 'italic' : 'normal',
            }}
          >
            {showAsRunning ? 'running…' : format(new Date(step.startTime), 'HH:mm:ss.SSS')}
          </Box>
        )}

        {/* Statement */}
        <Box sx={{ width: '28%', flexShrink: 0, fontFamily: 'monospace', fontSize: 12 }}>
          {step.statement}
        </Box>

        {/* Result */}
        <Box sx={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }}>
          {(() => {
            if (isSchema) {
              return (
                <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap' }}>{step.result}</pre>
              );
            }
            if (isTable) return <LogResultTable result={step.result} />;
            if (hasExtendedProps && appId) {
              return (
                <Typography variant="body2" fontSize={12}>
                  {step.result}{' '}
                  <a
                    href={`/a/${appId}/health/sfjobs?env=${environmentId}&step=${step.stepGuid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#6C63FF' }}
                  >
                    (details)
                  </a>
                </Typography>
              );
            }
            if (step.errorMessage || (step.completed && !step.succeeded && step.result)) {
              return (
                <Typography variant="body2" color="error" fontSize={12}>
                  {step.errorMessage || step.result}
                </Typography>
              );
            }
            return (
              <Typography variant="body2" fontSize={12}>
                {step.result}
              </Typography>
            );
          })()}
        </Box>

        {/* Rows Read */}
        <Box
          sx={{
            width: 70,
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 12,
            color: 'text.secondary',
          }}
        >
          {step.recordsRead > 0 ? step.recordsRead.toLocaleString() : ''}
        </Box>

        {/* Timer */}
        <Box
          sx={{
            width: 80,
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 12,
            color: 'text.secondary',
          }}
        >
          {step.timer != null ? step.timer.toFixed(5) : ''}
        </Box>
      </Box>
    );
  }
);

StepRow.displayName = 'StepRow';

/**
 * Reusable log viewer with auto-scroll, filtering, and search.
 * Used in both the Recipe Editor (RunningStatusPanel / ExecutionLogPanel)
 * and the Execution Details page.
 */
export const LogViewer: React.FC<LogViewerProps> = ({
  steps,
  isRunning,
  onRefresh,
  isFetching = false,
  showHeader = true,
  maxHeight = '100%',
  compact = false,
  appId,
  environmentId,
}) => {
  const [filter, setFilter] = useState<StepFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [followLogs, setFollowLogs] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevStepCountRef = useRef(0);
  const userScrolledRef = useRef(false);

  // Apply sequential step filtering to match dataProtect behavior:
  // - During execution: show completed steps + first uncompleted, stop after failure
  // - Completed execution: only show completed steps, stop after failure (unless onError=continue)
  const sequentialSteps = useMemo(() => {
    if (!steps || steps.length === 0) return [];
    const result: ExecutionStep[] = [];
    for (const step of steps) {
      if (step.completed) {
        result.push(step);
        if (!step.succeeded && step.onError !== 'continue') {
          break;
        }
      } else if (isRunning) {
        result.push(step);
        break;
      }
    }
    return result;
  }, [steps, isRunning]);

  // Filter steps (user-driven search and status filter on top of sequential logic)
  const filteredSteps = useMemo(() => {
    let result = sequentialSteps;

    if (filter !== 'all') {
      result = result.filter((step) => {
        switch (filter) {
          case 'success':
            return step.completed && step.succeeded;
          case 'error':
            return step.completed && !step.succeeded;
          case 'running':
            return !step.completed;
          default:
            return true;
        }
      });
    }

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (step) =>
          step.statement?.toLowerCase().includes(lower) ||
          step.result?.toLowerCase().includes(lower) ||
          step.errorMessage?.toLowerCase().includes(lower)
      );
    }

    return result;
  }, [sequentialSteps, filter, searchTerm]);

  // Count by status for filter badges (based on sequential steps)
  const statusCounts = useMemo(() => {
    if (!sequentialSteps.length) return { success: 0, error: 0, running: 0 };
    let success = 0;
    let error = 0;
    let running = 0;
    for (const step of sequentialSteps) {
      if (!step.completed) running++;
      else if (step.succeeded) success++;
      else error++;
    }
    return { success, error, running };
  }, [sequentialSteps]);

  // Auto-scroll when new steps arrive and followLogs is on
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !followLogs) return;

    const currentCount = filteredSteps.length;
    if (currentCount > prevStepCountRef.current) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
    prevStepCountRef.current = currentCount;
  }, [filteredSteps.length, followLogs]);

  // Auto-enable follow when execution starts
  useEffect(() => {
    if (isRunning) setFollowLogs(true);
  }, [isRunning]);

  // Detect user scrolling to auto-disable follow
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (!isAtBottom && followLogs && !userScrolledRef.current) {
      userScrolledRef.current = true;
      setFollowLogs(false);
    } else if (isAtBottom && !followLogs) {
      userScrolledRef.current = false;
      setFollowLogs(true);
    }
  }, [followLogs]);

  const handleFollowToggle = useCallback(() => {
    const newFollow = !followLogs;
    setFollowLogs(newFollow);
    userScrolledRef.current = false;
    if (newFollow && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [followLogs]);

  const toolbarHeight = compact ? 36 : 44;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: maxHeight, overflow: 'hidden' }}>
      {/* Toolbar */}
      {showHeader && (
        <Toolbar
          variant="dense"
          disableGutters
          sx={{
            px: 1,
            gap: 0.5,
            minHeight: toolbarHeight,
            maxHeight: toolbarHeight,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          {/* Step count */}
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: compact ? 12 : 13, mr: 1 }}>
            Steps: {filteredSteps.length}
            {steps && filteredSteps.length !== steps.length && ` / ${steps.length}`}
          </Typography>

          {isRunning && (
            <Chip
              label="Live"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 20, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
              icon={<CircularProgress size={10} color="inherit" />}
            />
          )}

          <Box sx={{ flex: 1 }} />

          {/* Search */}
          {showFilters && (
            <TextField
              size="small"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: 12, height: compact ? 28 : 32 },
                },
              }}
              sx={{ width: 180 }}
            />
          )}

          {/* Status filter */}
          {showFilters && (
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, v) => v && setFilter(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.25,
                  fontSize: 11,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="success">
                <CheckIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                {statusCounts.success}
              </ToggleButton>
              <ToggleButton value="error">
                <ErrorIcon sx={{ fontSize: 14, mr: 0.5, color: 'error.main' }} />
                {statusCounts.error}
              </ToggleButton>
              {isRunning && (
                <ToggleButton value="running">
                  <CircularProgress size={12} sx={{ mr: 0.5 }} />
                  {statusCounts.running}
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          )}

          {/* Toggle filter panel */}
          <Tooltip title="Filter logs">
            <IconButton
              size="small"
              onClick={() => setShowFilters(!showFilters)}
              color={showFilters ? 'primary' : 'default'}
            >
              <FilterListIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Follow toggle */}
          <Tooltip title={followLogs ? 'Auto-scroll ON' : 'Auto-scroll OFF'}>
            <IconButton
              size="small"
              onClick={handleFollowToggle}
              color={followLogs ? 'primary' : 'default'}
            >
              <FollowIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Refresh */}
          {onRefresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={onRefresh} disabled={isFetching}>
                  {isFetching ? (
                    <CircularProgress size={16} />
                  ) : (
                    <RefreshIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Toolbar>
      )}

      {/* Column headers */}
      <Box
        sx={{
          display: 'flex',
          px: 1,
          py: 0.5,
          fontSize: 11,
          fontWeight: 600,
          color: 'text.secondary',
          bgcolor: '#fef7e0',
          borderBottom: '1px solid #fdd663',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 24 }} />
        {steps?.[0]?.startTime && <Box sx={{ width: 85 }}>Time</Box>}
        <Box sx={{ width: '28%' }}>Statement</Box>
        <Box sx={{ flex: 1 }}>Result</Box>
        <Box sx={{ width: 70, textAlign: 'right' }}>Rows</Box>
        <Box sx={{ width: 80, textAlign: 'right' }}>Timer</Box>
      </Box>

      {/* Log entries */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {!steps && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {steps && filteredSteps.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                if (searchTerm || filter !== 'all') return 'No steps match current filters';
                if (isRunning) return 'Waiting for steps...';
                return 'No steps recorded';
              })()}
            </Typography>
          </Box>
        )}

        {filteredSteps.map((step, i) => (
          <StepRow
            key={step.stepGuid || `step-${i}`}
            step={step}
            index={i}
            isRunning={isRunning}
            appId={appId}
            environmentId={environmentId}
          />
        ))}

        {/* Running indicator at bottom */}
        {isRunning && filteredSteps.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              color: 'text.secondary',
              fontSize: 12,
            }}
          >
            <CircularProgress size={14} />
            <span>Execution in progress...</span>
          </Box>
        )}
      </Box>
    </Box>
  );
};
